import { Component, ElementRef, Inject, Injector, OnDestroy, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatStepper } from '@angular/material/stepper';
import * as Papa from 'papaparse';
import { ConfirmationComponent, FirebaseService, UploadTypeEnum } from '../../../../../public-api';
import { AbstractComponent } from '../../../../commons/abstract.component';
import { UtilHelper } from '../../../../helpers/util.helper';
import { Contact } from '../../../../models/contact.model';
import { Importation } from '../../../../models/importation.model';
import { ContactGroupService } from '../../../../services/contact-group.service';
import { ContactService } from '../../../../services/contact.service';
import { DepartmentService } from '../../../../services/department.service';
import { ImportationService } from '../../../../services/importation.service';
import { TagService } from '../../../../services/tag.service';
import { AlertModalComponent } from '../../alert-modal/alert-modal.component';

type ProfileField = 'phone' | 'name' | 'email' | 'tags' | 'ignore';

interface MappingRow {
  csvColumn: string;
  sample: string;
  mappedTo: ProfileField;
}

interface CsvRow {
  telefone?: string;
  email?: string;
  nome?: string;
  etiquetas?: string;
  [key: string]: unknown;
}


@Component({
    selector: 'contact-upload-csv-component',
    templateUrl: './contact-upload-csv.v2.component.html',
    // templateUrl: './contact-upload-csv.component.html',
    styleUrls: ['./contact-upload-csv.v2.component.scss'],
    // styleUrls: ['./contact-upload-csv.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContactUploadCsvComponent extends AbstractComponent implements OnDestroy {

  rawRows: any[] = [];
  rawHeaders: string[] = [];
  mappings: MappingRow[] = [];
  applyCountryPrefix = false;
  countryPrefix = '55';

  csvContent: any[] = [];
  paginatedContent: any[] = [];
  pageSize = 10;
  currentPage = 0;

  showOnlyErrors = false;

  url = '';
  filename = '';
  contentType = '';
  fileSizeMb = '';
  fileLoaded = false;
  mapCSVColumns: Map<string, string> = new Map();
  rawCsvText: string = undefined;
  activeTab: 'import' | 'rules' = 'import';

  stepIndex = 0;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('inputFile', { static: false }) inputFile: ElementRef<HTMLInputElement>;

  uploadCtrl: ReturnType<FormBuilder['group']>;
  mapColsCtrl: FormGroup<{ mapped: FormControl<boolean>; }>;

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<ContactUploadCsvComponent>,
    public contactService: ContactService,
    public contactGrupoService: ContactGroupService,
    public tagService: TagService,
    public departmentService: DepartmentService,
    public dialog: MatDialog,
    private modalAlert: MatDialog,
    private firebaseService: FirebaseService,
    private importationService: ImportationService,
    @Inject(MAT_DIALOG_DATA) public data: Contact,
  ) {
    super(injector);
    this.uploadCtrl = this.formBuilder.group({ uploaded: [false] });
    this.mapColsCtrl = this.formBuilder.group({ mapped: [false] });
  }

  ngOnInit() { }

  override ngOnDestroy(): void { }

  onClose(): void {
    this.dialogRef.close();
  }


  handleFileSelect(event: any): void {
    const file = event.target.files[0]

    if (!file) { this.fileLoaded = false; return; }

    const fileTypeLimit = UtilHelper.getFileTypeLimit(file.type);
    if (fileTypeLimit && file.size > fileTypeLimit.maxSize) {
      this.openModalAlert(fileTypeLimit.alertTitle, fileTypeLimit.alertMessage);
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      try {
        this.loading = true;

        const dateMilisecond = new Date().getTime();
        const filename: string = `${file.name}_${dateMilisecond}`;
        this.filename = file.name;
        this.contentType = file.type;
        this.fileSizeMb = this.formatBytes(file.size);

        this.firebaseService.uploadFile(file, filename, UploadTypeEnum.CSV).then((snapshot: { url: string }) => {
          this.url = snapshot.url;
          this.loading = false;
        });

        this.rawCsvText = (reader.result as string) ?? '';

      } catch (error) {
        this.alertService.error(this.t('shared.contactUploadCsv.errorProcessingFile'));
      } finally {
        this.fileLoaded = true;
        this.loading = false;
      }
    };

    reader.readAsText(file, 'UTF-8');
  }

  onContinue(stepper: MatStepper): void {
    this.parseCsvAndStoreRaw();
    stepper.next();
  }

  next() { if (this.canGoNext()) this.stepIndex++; }
  prev() { if (this.stepIndex > 0) this.stepIndex--; }
  canGoNext(): boolean {
    if (this.stepIndex === 0) return !!this.fileLoaded && !this.loading;
    if (this.stepIndex === 1) return this.isMappingValid();
    return true;
  }

  onContinueFromStep1() {
    if (this.canGoNext()) {
      this.parseCsvAndStoreRaw();
      this.next();
    }
  }
  onContinueFromStep2() {
    this.applyMappingAndBuildRows();
    if (this.canGoNext()) this.next();
  }

  parseCsvAndStoreRaw(): void {
    if (!this.rawCsvText || typeof this.rawCsvText !== 'string') {
      this.openModalAlert(this.t('shared.contactUploadCsv.missingFileTitle'), this.t('shared.contactUploadCsv.missingFileMessage'));
      return;
    }
    Papa.parse(this.rawCsvText, {
      header: true,
      skipEmptyLines: true,
      complete: (results: Papa.ParseResult<any>) => {
        const rows = (results.data as any[]).filter(r => !!r && Object.keys(r).length > 1);
        this.rawRows = rows;
        this.rawHeaders = results.meta.fields || Object.keys(rows[0] || {});
        this.buildDefaultMappings();
        this.uploadCtrl.patchValue({ uploaded: true });
      },
      error: (err) => {
        this.alertService.error(this.t('shared.contactUploadCsv.errorProcessingCsv'));
      }
    });
  }

  clearFile(): void {
    this.filename = '';
    this.fileSizeMb = '';
    this.fileLoaded = false;
    this.contentType = '';
    this.url = '';
    this.rawRows = [];
    this.rawHeaders = [];
    this.mappings = [];
    this.csvContent = [];
    this.paginatedContent = [];
    this.currentPage = 0;
    this.uploadCtrl.patchValue({ uploaded: false });
    if (this.inputFile && this.inputFile.nativeElement) {
      this.inputFile.nativeElement.value = '';
    }
  }

  private buildDefaultMappings() {
    const infer = (col: string): ProfileField => {
      const c = (col || '').toLowerCase().trim();
      if (/(telefone|celular|phone|whats|zap|numero|número)/.test(c)) return 'phone';
      if (/(nome|name|first\s*name|nome\s*completo)/.test(c)) return 'name';
      if (/(email|e-mail)/.test(c)) return 'email';
      if (/(etiquetas|tags|label|grupo|lista)/.test(c)) return 'tags';
      return 'ignore';
    };

    let headers = [...this.rawHeaders];
    let dataStart = 0;

    if (headers.every(h => /^\d+$/.test(h)) && this.rawRows.length) {
      const firstRow = this.rawRows[0];
      const firstValues = headers.map(h => String(firstRow[h] ?? '').trim());
      if (firstValues.some(v => /(email|telefone|celular|whats|nome|empresa|estado|tags)/i.test(v))) {
        headers = firstValues.map((v, i) => v || `col_${i + 1}`);
        dataStart = 1;
      }
    }

    this.mappings = headers.map((col, i) => {
      let sample = '';
      for (let r = dataStart; r < this.rawRows.length; r++) {
        const row = this.rawRows[r];
        const val = row[col] ?? row[this.rawHeaders[i]];
        if (val) { sample = String(val).trim(); break; }
      }
      return { csvColumn: col, sample, mappedTo: infer(col) };
    });

    this.normalizeUniqueTargets();
  }

  private normalizeUniqueTargets() {
    const uniques: ProfileField[] = ['phone', 'name', 'email', 'tags'];
    for (const target of uniques) {
      const indices = this.mappings
        .map((m: any, i: number) => ({ i, m }))
        .filter(x => x.m.mappedTo === target)
        .map(x => x.i);
      if (indices.length > 1) {
        indices.slice(1).forEach(i => (this.mappings[i].mappedTo = 'ignore'));
      }
    }
  }

  isMappingValid(): boolean {
    return ['phone', 'name'].every(field => this.mappings.some(m => m.mappedTo === field));
  }

  setCountryPrefixFromColumn(m: MappingRow) {
    const s = (m.sample || '').toString().trim();
    const match = s.match(/^(\d{1,3})\d{8,}$/);
    if (match) this.countryPrefix = match[1];
  }

  applyMappingByColumn(csvColumn: string, contactColumn: string) {
    this.mapCSVColumns.set(contactColumn, csvColumn);

    const mapping = this.mappings.find(m => m.csvColumn === csvColumn);
    if (mapping) mapping.mappedTo = contactColumn as ProfileField;
  }

  applyMappingAndBuildRows() {
    const mappingMap = new Map<ProfileField, string>(
      (this.mappings || []).map(m => [m.mappedTo, m.csvColumn] as const)
    );

    const colPhone = mappingMap.get('phone');
    const colName = mappingMap.get('name');
    const colEmail = mappingMap.get('email');
    const colTags = mappingMap.get('tags');

    const seenPhones = new Set<string>();
    const emailPattern = this.emailRegex;

    this.csvContent = (this.rawRows || [])
      .map((row: Record<string, any>, index: number) => {
        const cell = (col?: string) =>
          (col ? (row[col] ?? '') : '').toString().trim();

        let phone = cell(colPhone).replace(/\D+/g, '');

        if (this.applyCountryPrefix && phone && !phone.startsWith(this.countryPrefix)) {
          phone = `${this.countryPrefix}${phone}`;
        }

        const name = cell(colName);
        const email = cell(colEmail);
        const tags = cell(colTags);

        let isEmailValid = true;
        if (!!email) {
          isEmailValid = emailPattern.test(email);
        }

        const phoneOnlyNumbers = phone.replace(/[^0-9.]/g, '');
        let isPhoneLengthValid = false;
        if (!!phoneOnlyNumbers) {
          isPhoneLengthValid = true;
          if (phone.startsWith('55')) {
            isPhoneLengthValid = phone.length === 12 || phone.length === 13;
          }
        }

        if (phone) {
          if (seenPhones.has(phone)) return null;
          seenPhones.add(phone);
        }

        const hasError = !isPhoneLengthValid || !name || !isEmailValid;

        return phone
          ? { name: name || phone, phone, email, tags, index, hasError }
          : null;
      })
      .filter((x): x is NonNullable<typeof x> => !!x);

    this.currentPage = 0;
    this.updatePaginatedContent();
  }

  updatePaginatedContent(): void {
    let data = this.csvContent;
    if (this.showOnlyErrors) {
      data = data.filter((row: any) => row.hasError);
    }
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;
    this.paginatedContent = data.slice(start, end);
  }

  filterOnlyErrors(): void {
    this.showOnlyErrors = !this.showOnlyErrors;
    this.currentPage = 0;
    this.updatePaginatedContent();
  }

  removeAllErrors(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.csvContent = this.csvContent.filter((i: any) => !i.hasError);
        this.filterOnlyErrors();
      }
    });
  }

  handlePage(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedContent();
  }

  delete(item: any) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.csvContent = this.csvContent.filter((i: any) => i.index !== item.index);
        this.updatePaginatedContent();
      }
    });
  }

  getNumberContactErrors(): number {
    return this.csvContent.filter((i: any) => i.hasError).length;
  }

  save() {
    this.applyMappingAndBuildRows();
    if (!this.csvContent?.length) {
      this.alertService.error(this.t('shared.contactUploadCsv.noContactsLoaded'));
      return;
    }
    if (this.csvContent.length - this.getNumberContactErrors() === 0) {
      this.alertService.error(this.t('shared.contactUploadCsv.noValidContactsLoaded'));
      return;
    }

    const mappingMap = new Map<ProfileField, string>(
      (this.mappings || []).map(m => [m.mappedTo, m.csvColumn] as const)
    );

    const importation = {
      filename: this.filename,
      contentType: this.contentType,
      url: this.url,
      metadata: {
        totalSend: this.csvContent.length,
        totalExists: 0,
        totalInserted: 0,
        totalUpdated: 0,
        totalCreatedTag: 0,
        totalError: this.getNumberContactErrors(),
        mappings: Object.fromEntries(mappingMap)
      }
    } as Importation

    this.importationService.create(importation).subscribe({
      next: () => this.alertService.success(this.t('shared.contactUploadCsv.importScheduledSuccess')),
      error: () => this.alertService.error(this.t('shared.contactUploadCsv.importScheduledError')),
      complete: () => this.dialogRef.close()
    });
  }

  private openModalAlert(title: string, message: string): void {
    const dialogRefAlert = this.modalAlert.open(AlertModalComponent, {
      width: '600px',
      maxHeight: '300px',
      data: { title, message },
    });
    dialogRefAlert.afterClosed().subscribe(() => this.inputFile.nativeElement.value = '');
  }

  private formatBytes(bytes: number): string {
    const KB = 1024, MB = KB * 1024, GB = MB * 1024;
    if (bytes >= KB) return `${(bytes / KB).toFixed(2)} KB`;
    return `${bytes} B`;
  }
}
